<template>
  <div class="config-creation">
    <v-card>
      <v-card-title>Paramétrage de la nouvelle configuration </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              label="Nom de la configuration"
              v-model="config.name"
              outlined
              hide-details
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <source-filter
              :initialValue="config.news_radar_source_id"
              :exclude="externalSources"
              :multiple="false"
              :zindex="zIndex.modale"
              :noUrl="true"
              @valueChanged="updateSource"
            />
          </v-col>
          <v-col cols="12" md="6">
            <site-filter
              :initialValue="config.site_id"
              :multiple="false"
              :noUrl="true"
              :dependsOnSiteGroups="false"
              :zindex="zIndex.modale"
              @valueChanged="updateSite"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-combobox
              outlined
              v-model="config.entry"
              :items="page.entries"
              :menu-props="{ maxHeight: '400', zIndex: zIndex.modale }"
              label="Entrée"
              :placeholder="placeHolder"
              allow-overflow
              hide-selected
              hide-no-data
              :search-input.sync="page.searchEntry"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              outlined
              hide-details
              v-model="config.media_type"
              :items="mediaTypes"
              :disabled="!sourceCanCarryVideo"
              :menu-props="{ zIndex: zIndex.modale }"
              label="Support"
              :hint="mediaTypeHint"
              persistent-hint
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-row justify="center" class="pb-6">
          <v-btn class="ml-6 mr-6 secondary" @click="saveConfig"
            >Enregistrer</v-btn
          >
          <v-btn class="ml-6 mr-6 primary" @click="cancelConfig">Annuler</v-btn>
        </v-row>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import { computed, reactive, watch } from "@vue/composition-api";

import useAxios from "@/hooks/useAxios";

import SiteFilter from "@/components/common/filters/SiteFilter.vue";
import SourceFilter from "@/components/news-radar/common/filters/SourceFilter.vue";
import validateConfig from "@/components/news-radar/config/helpers/validateConfig";
import {
  NEWS_RADAR_EXTERNAL_SOURCES,
  NEWS_RADAR_MEDIA_TYPES,
  NEWS_RADAR_VIDEO_SOURCES,
  zIndex,
} from "@/utils/constants";

export default {
  name: "ConfigCreation",
  components: {
    SiteFilter,
    SourceFilter,
  },
  setup(props, { emit }) {
    const { axiosGet, axiosPost } = useAxios();

    const config = reactive({
      name: "",
      entry: "",
      site_id: 0,
      news_radar_source_id: 0,
      // Écrit par défaut : c'est aussi le défaut côté base, donc une config
      // créée sans y toucher se comporte exactement comme avant.
      media_type: "written",
    });

    const page = reactive({
      entries: [],
      searchEntry: "",
      source: {
        id: 0,
        name: "",
      },
    });

    // =>  METHODS
    const resetState = () => {
      config.name = "";
      config.entry = "";
      config.site_id = 0;
      config.news_radar_source_id = 0;
      config.media_type = "written";

      page.source = { id: 0, name: "" };
      page.entries = [];
      page.searchEntry = "";
    };

    const updateSite = (value) => {
      config.site_id = value;
    };

    const updateSource = async (value) => {
      page.source = value;
      config.news_radar_source_id = value.id;

      // Changer pour une source qui ne porte pas de vidéo doit ramener le
      // support à l'écrit : sinon le select part en grisé sur « Vidéo », et
      // c'est cette valeur-là qui serait enregistrée.
      if (!NEWS_RADAR_VIDEO_SOURCES.includes(value.name)) {
        config.media_type = "written";
      }

      const { data } = await axiosGet("/news-radar-sources/source-entries", { source_name: value.name});
      page.entries = data;
    };

    const cancelConfig = async () => {
      resetState();
      emit("close-dialog");
    };

    const saveConfig = async () => {
      const validation = validateConfig(config, page.source.name);
      if (validation !== true) {
        emit("warning", validation);
      }

      try {
        await axiosPost("/news-radar-configs/", {
          ...config,
        });
        resetState();
        emit("saved");
        emit("close-dialog");
      } catch (error) {
        emit("error");
      }
    };

    // =>  COMPUTED
    const sourceCanCarryVideo = computed(() =>
      NEWS_RADAR_VIDEO_SOURCES.includes(page.source.name)
    );

    const mediaTypeHint = computed(() =>
      sourceCanCarryVideo.value
        ? "Un même compte peut être suivi en écrit ET en vidéo : deux configs."
        : "Cette source ne porte pas de vidéo transcriptible."
    );

    const placeHolder = computed(() => {
      switch (page.source.name) {
        case "Youtube":
          return "https://www.youtube.com/@ChannelToFollow";

        case "Reddit":
          return "r/RedditChannel";

        case "Gtrend":
          return "Google Trends - Ma trend";

        case "Marfeel":
          return "Config JSON";
        
        case "Instagram (via RssApp)":
          return "https://www.instagram.com/accountToFollow/";
        
        case "X (via RssApp)":
          return "https://x.com/accountToFollow";
        
        case "Tiktok (via RssApp)":
          return "https://www.tiktok.com/accountToFollow/";

        case "Linkedin (via RssApp)":
          return "https://www.linkedin.com/company/nom-entreprise";
        
        case "Website (via RssApp)":
          return "https://website.url.to-parse";

        case "Concurrency":
        default:
          return "https://mon.flux/rss.xml";
      }
    });

    // =>  WATCH
    watch(() => page.searchEntry, (newValue) => config.entry = newValue);

    // =>  HOOKS

    // =>  DATA
    return {
      cancelConfig,
      config,
      externalSources: NEWS_RADAR_EXTERNAL_SOURCES,
      mediaTypeHint,
      mediaTypes: NEWS_RADAR_MEDIA_TYPES,
      page,
      placeHolder,
      saveConfig,
      sourceCanCarryVideo,
      updateSite,
      updateSource,
      zIndex,
    };
  },
};
</script>

<style scoped>
.config-creation {
  max-width: 1000px;
  margin: 0 auto;
}
</style>
